import { apiSlice } from "api/api.slice";
import { QUERY_LIMIT } from "api/constants";
import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { type Bookmark, type GetBookmarksResponse, getBookmarksResponseSchema } from "api/schemas/bookmarksApiSchemas";
import { toggleBookmarkFulfilled } from "domains/bookmarks/actions";

export const bookmarksApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getBookmarks: build.infiniteQuery<Bookmark[], undefined, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: () => ({
        url: `/bookmark`,
        schema: getBookmarksResponseSchema,
      }),
      transformResponse: (response: GetBookmarksResponse) => response.bookmarks,
      providesTags: ["Bookmarks"],
    }),
    addKnowledgeToBookmarks: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        method: "PUT",
        url: `/bookmark/${id}`,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        dispatch(toggleBookmarkFulfilled({ id, isBookmarked: true }));
        const patchResult = dispatch(
          knowledgeApiSlice.util.updateQueryData("getKnowledgeById", { id }, (draft) => {
            if (draft) {
              draft.is_bookmarked = true;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          dispatch(toggleBookmarkFulfilled({ id, isBookmarked: false }));
        }
      },
      invalidatesTags: ["Bookmarks"],
    }),
    removeKnowledgeFromBookmarks: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        method: "DELETE",
        url: `/bookmark/${id}`,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        dispatch(toggleBookmarkFulfilled({ id, isBookmarked: false }));

        const getKnowledgeByIdOptimisticUpdate = dispatch(
          knowledgeApiSlice.util.updateQueryData("getKnowledgeById", { id }, (draft) => {
            if (draft) {
              draft.is_bookmarked = false;
            }
          }),
        );

        const getBookmarksOptimisticUpdate = dispatch(
          bookmarksApiSlice.util.updateQueryData("getBookmarks", undefined, (draft) => {
            draft.pages = draft.pages.map((page) => page.filter((bookmark) => bookmark.id !== id));
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          getKnowledgeByIdOptimisticUpdate.undo();
          getBookmarksOptimisticUpdate.undo();
          dispatch(toggleBookmarkFulfilled({ id, isBookmarked: true }));
        }
      },
    }),
  }),
});
